// enhancementUtils.js
/**
 * Utilities for context-aware prompt enhancement
 */

/**
 * Analyze a message to determine if it's likely a follow-up question
 * 
 * @param {string} message - The message to analyze
 * @param {Array} previousMessages - Previous messages in the conversation
 * @returns {boolean} - Whether the message is likely a follow-up
 */
export const isFollowUpQuestion = (message, previousMessages = []) => {
  if (!message || previousMessages.length === 0) return false;
  
  // Normalize message for analysis
  const normalizedMessage = message.trim().toLowerCase();
  
  // Check for common follow-up indicators
  const followUpIndicators = [
    // Pronouns and demonstratives that refer to previously mentioned items
    'this', 'these', 'those', 'that', 'it', 'they', 'them',
    // Phrases that suggest continuation
    'what about', 'how about', 'and also', 'furthermore', 'additionally',
    'more specifically', 'furthermore', 'moreover', 'in addition',
    // Questions without sufficient context
    'why', 'how', 'when', 'where', 'which one', 'which ones', 'what else',
    'tell me more', 'can you elaborate', 'explain', 'more details',
    // Comparative language
    'instead', 'rather', 'compared to', 'versus', 'vs', 'better', 'worse',
    'more', 'less', 'alternative', 'alternatives', 'option', 'options', 'compare'
  ];
  
  // Check if message starts with or contains follow-up indicators
  const containsFollowUpIndicator = followUpIndicators.some(indicator => {
    return normalizedMessage.startsWith(indicator) || 
           normalizedMessage.includes(` ${indicator} `);
  });
  
  // Short messages are often follow-ups
  const isShortQuery = normalizedMessage.split(' ').length <= 7;
  
  // Message contains references that only make sense in context
  const hasReferentialTerms = /\b(it|this|these|those|that|they|them)\b/i.test(normalizedMessage);
  
  // If message is short AND has referential terms, it's very likely a follow-up
  if (isShortQuery && hasReferentialTerms) {
    return true;
  }
  
  // If message directly mentions options/choices from previous message
  if (containsFollowUpIndicator && previousMessages.length > 0) {
    return true;
  }
  
  // Look for references to specific content in previous messages
  const previousContent = previousMessages
    .filter(msg => msg.role === 'assistant')
    .map(msg => msg.content || '')
    .join(' ')
    .toLowerCase();
  
  // Extract potential topics from previous messages (simple approach)
  const potentialTopics = extractKeyTerms(previousContent);
  
  // Check if current message references topics from previous messages
  const containsSharedTopic = potentialTopics.some(topic => 
    normalizedMessage.includes(topic)
  );
  
  return containsSharedTopic || containsFollowUpIndicator;
};

/**
 * Extract the main topic or entity from previous messages
 * 
 * @param {Array} messages - Previous messages in the conversation
 * @returns {string} - Extracted main topic
 */
export const extractMainTopic = (messages = []) => {
  if (!messages || messages.length === 0) return '';
  
  // Join all messages together, prioritizing user messages
  const allContent = messages
    .map(msg => msg.content || '')
    .join(' ');
  
  // Extract candidate topics (simple implementation)
  const candidateTopics = extractKeyTerms(allContent);
  
  // Return the top candidate or empty string if none found
  return candidateTopics[0] || '';
};

/**
 * Extract key terms from text that could be topics
 * 
 * @param {string} text - Text to analyze
 * @returns {Array} - Array of potential topic terms
 */
const extractKeyTerms = (text = '') => {
  if (!text) return [];
  
  // Remove common words, punctuation
  const normalizedText = text.toLowerCase()
    .replace(/[^\w\s]/g, ' ')
    .replace(/\s+/g, ' ');
  
  // Common English stop words to filter out
  const stopWords = new Set([
    'a', 'an', 'the', 'and', 'but', 'or', 'for', 'nor', 'on', 'at', 'to', 'by',
    'is', 'are', 'am', 'was', 'were', 'be', 'been', 'being',
    'in', 'on', 'at', 'by', 'with', 'about', 'against', 'between',
    'into', 'through', 'during', 'before', 'after', 'above', 'below',
    'i', 'you', 'he', 'she', 'it', 'we', 'they',
    'me', 'him', 'her', 'us', 'them',
    'this', 'that', 'these', 'those',
    'which', 'who', 'whom', 'whose',
    'what', 'where', 'when', 'why', 'how',
    'all', 'any', 'both', 'each',
    'few', 'many', 'some',
    'can', 'could', 'may', 'might', 'must', 'shall', 'should', 'will', 'would',
    'do', 'does', 'did', 'have', 'has', 'had',
    'go', 'went', 'gone', 'get', 'got', 'gotten',
    'make', 'made', 'say', 'said', 'see', 'saw', 'seen',
    'come', 'came', 'know', 'knew', 'known',
    'think', 'thought', 'use', 'used'
  ]);
  
  // Split into words, filter out stop words
  const words = normalizedText.split(' ')
    .filter(word => word.length > 2) // Filter out very short words
    .filter(word => !stopWords.has(word)); // Filter out stop words
  
  // Count frequency of each word
  const wordFrequency = {};
  words.forEach(word => {
    wordFrequency[word] = (wordFrequency[word] || 0) + 1;
  });
  
  // Sort by frequency
  const sortedTerms = Object.entries(wordFrequency)
    .sort((a, b) => b[1] - a[1])
    .map(entry => entry[0]);
  
  // Return top terms
  return sortedTerms.slice(0, 5);
};

/**
 * Generate an enhanced prompt by using Claude to analyze the conversation
 * 
 * @param {string} message - The message to enhance
 * @param {Array} previousMessages - Previous messages in the conversation
 * @returns {string} - Enhanced prompt for Claude
 */
export const generateEnhancementPrompt = (message, previousMessages = []) => {
  // Get the full conversation context
  const contextMessages = getRelevantContextMessages(previousMessages);
  
  // Format the conversation as a readable exchange
  const conversationHistory = contextMessages.map(msg => 
    `${msg.role === 'user' ? 'Human' : 'Assistant'}: ${msg.content}`
  ).join('\n\n');
  
  // Let Claude analyze the conversation itself  
  return `You are an expert prompt engineer with exceptional skills in creating detailed, context-aware prompts that generate high-quality AI responses. I need you to transform a user's brief query into a comprehensive, well-structured prompt.

CONVERSATION HISTORY:
${conversationHistory}

USER'S ORIGINAL REQUEST:
"${message.trim()}"

Please create a significantly enhanced version that will generate the most helpful, detailed, and accurate response possible. Your enhanced prompt should:

1. Thoroughly incorporate the conversation's domain knowledge and context
   - Identify the primary domain (e.g., programming, finance, basketball, medicine)
   - Reference specific entities, concepts, and terminology already discussed
   - Maintain continuity with previous exchanges

2. Add necessary specificity and details
   - Expand abbreviated or vague questions with precise parameters
   - Include relevant constraints or requirements implied by the conversation
   - Specify the scope and depth of the desired response

3. Improve structure and clarity
   - Organize complex requests into logical components
   - Use numbered lists or bullet points for multi-part requests
   - Remove ambiguities that could lead to misinterpretation

4. Request particular output formats when appropriate
   - Suggest specific formats (tables, lists, step-by-step instructions)
   - Include sample structures if helpful
   - Specify the desired level of detail or technical depth

5. Incorporate domain-specific best practices
   - Use proper terminology and conventions from the domain
   - Frame the request using domain-appropriate methodologies
   - Add relevant parameters that domain experts would typically consider

Your output should be ONLY the enhanced prompt with no explanations, preambles, or additional text. The enhanced prompt should be substantially more detailed than the original while maintaining its essential intent.`;
};

/**
 * Get the most relevant previous messages for context
 * 
 * @param {Array} messages - All previous messages
 * @returns {Array} - Most relevant messages for context
 */
const getRelevantContextMessages = (messages = []) => {
  if (!messages || messages.length === 0) return [];
  
  // If conversation is short, include all messages
  if (messages.length <= 6) return messages;
  
  // For longer conversations, include:
  // 1. The first user message (establishes initial context)
  // 2. The most recent 4-5 messages (immediate context)
  
  const firstUserMessage = messages.find(msg => msg.role === 'user');
  const recentMessages = messages.slice(-5);
  
  // If the first user message is already in recent messages, just return those
  if (recentMessages.some(msg => msg === firstUserMessage)) {
    return recentMessages;
  }
  
  // Otherwise include first message plus recent messages
  return [firstUserMessage, ...recentMessages];
};

export default {
  isFollowUpQuestion,
  extractMainTopic,
  generateEnhancementPrompt
};